import {Injectable, inject} from '@angular/core';
import {AuthService} from '../auth/auth.service';
import {User} from '../../shared/model/user';
import {Organization} from '../../shared/model/organization';
import {OrganizationSummary} from '../../shared/model/organization-summary';
import {GameTable} from '../../shared/model/game-table';
import {Event} from '../../shared/model/event';
import {
  OrgCapability,
  OrgRoleCapabilities,
  OrgRolePower,
  SystemCapability,
  SystemRoleCapabilities,
  SystemPower,
} from '../../shared/model/permissions';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private authService = inject(AuthService);

  // =========================================================================
  // AUTH STATE HELPERS
  // =========================================================================

  public getCurrentUser(): User | undefined {
    return this.authService.getCurrentUser();
  }

  public isLogged(): boolean {
    return this.authService.isLogged();
  }

  /**
   * Ritorna il userType dell'utente corrente (stringa come 'ADMINISTRATOR', 'ASSOCIATION', etc.) o null se anonimo.
   */
  public getSystemUserType(): string | null {
    const user = this.authService.getCurrentUser();
    return user ? user.userType : null;
  }

  // =========================================================================
  // SYSTEM LEVEL (POWER & CAPABILITIES)
  // =========================================================================

  /**
   * Ritorna il System Power numerico dell'utente corrente (0 se anonimo).
   */
  public getSystemPower(): number {
    const user = this.authService.getCurrentUser();
    if (!user || !user.userType) {
      return 0;
    }
    return (SystemPower as Record<string, number>)[user.userType] ?? 0;
  }

  /**
   * Verifica se l'utente corrente ha almeno il System Power specificato.
   */
  public hasSystemPower(minPower: number): boolean {
    return this.getSystemPower() >= minPower;
  }

  /**
   * Verifica se l'utente corrente possiede una specifica SystemCapability.
   */
  public hasSystemCapability(capability: SystemCapability): boolean {
    const user = this.authService.getCurrentUser();
    if (!user || !user.userType) {
      return false;
    }
    // Administrator ha tutte le capability
    if (user.userType === 'ADMINISTRATOR') {
      return true;
    }
    const caps = SystemRoleCapabilities[user.userType] ?? [];
    return caps.includes(capability);
  }

  public canManageUsers(): boolean {
    return this.hasSystemCapability(SystemCapability.MANAGE_USERS);
  }

  public canChangeUserType(): boolean {
    return this.hasSystemCapability(SystemCapability.MANAGE_USERS);
  }

  // =========================================================================
  // ORGANIZATION LEVEL (POWER & CAPABILITIES)
  // =========================================================================

  /**
   * Determina il ruolo dell'utente corrente all'interno di una specifica organizzazione.
   */
  public getOrgRole(org: Organization | undefined | null): 'OWNER' | 'ADMIN' | 'MODERATOR' | 'MEMBER' | null {
    if (!org) {
      return null;
    }
    if (org.owner) return 'OWNER';
    if (org.admin) return 'ADMIN';
    if (org.moderator) return 'MODERATOR';
    if (org.member) return 'MEMBER';
    return null;
  }

  /**
   * Ritorna il power numerico dell'utente all'interno di un'organizzazione (0 se non membro).
   */
  public getOrgPower(org: Organization | undefined | null): number {
    // System Administrator / Moderator bypass
    if (this.hasSystemPower(SystemPower.ADMINISTRATOR)) {
      return OrgRolePower.OWNER;
    }
    if (this.hasSystemPower(SystemPower.MODERATOR)) {
      return OrgRolePower.ADMIN;
    }

    const role = this.getOrgRole(org);
    if (!role) {
      return 0;
    }
    return OrgRolePower[role] ?? 0;
  }

  /**
   * Verifica se l'utente ha almeno un certo livello di Organization Power.
   */
  public hasOrgPower(org: Organization | undefined | null, minPower: number): boolean {
    return this.getOrgPower(org) >= minPower;
  }

  /**
   * Verifica se l'utente possiede una specifica OrgCapability all'interno dell'organizzazione.
   */
  public hasOrgCapability(org: Organization | undefined | null, capability: OrgCapability): boolean {
    // System Administrator ha pieno controllo
    if (this.hasSystemPower(SystemPower.ADMINISTRATOR)) {
      return true;
    }
    // System Moderator ha accesso a tutto tranne eliminazione org
    if (this.hasSystemPower(SystemPower.MODERATOR) && capability !== OrgCapability.ORG_DELETE) {
      return true;
    }

    const role = this.getOrgRole(org);
    if (!role) {
      return false;
    }
    const caps = OrgRoleCapabilities[role] ?? [];
    return caps.includes(capability);
  }

  // =========================================================================
  // RELATIONAL CHECKS (TEAMSPEAK-STYLE POWER HIERARCHY)
  // =========================================================================

  /**
   * Converte una stringa di ruolo org (es. 'OWNER', 'ADMIN', 'MODERATOR', 'MEMBER') nel suo power numerico.
   */
  public parseOrgRolePower(roleName: string | undefined | null): number {
    if (!roleName) return 0;
    const normalized = roleName.toUpperCase();
    return (OrgRolePower as Record<string, number>)[normalized] ?? 0;
  }

  /**
   * Verifica se l'attore può modificare o rimuovere un membro target (regola: actorPower > targetPower).
   */
  public canManageMember(
    org: Organization | undefined | null,
    targetRoleName: string | undefined | null,
  ): boolean {
    if (this.hasSystemPower(SystemPower.ADMINISTRATOR)) {
      return true;
    }
    if (!this.hasOrgCapability(org, OrgCapability.ORG_MEMBER_MANAGE)) {
      return false;
    }
    const actorPower = this.getOrgPower(org);
    const targetPower = this.parseOrgRolePower(targetRoleName);
    return actorPower > targetPower;
  }

  /**
   * Verifica se l'attore può assegnare un determinato ruolo (regola: newRolePower < actorPower, tranne OWNER).
   */
  public canAssignRole(
    org: Organization | undefined | null,
    newRoleName: string | undefined | null,
  ): boolean {
    if (this.hasSystemPower(SystemPower.ADMINISTRATOR)) {
      return true;
    }
    if (!this.hasOrgCapability(org, OrgCapability.ORG_ROLE_ASSIGN)) {
      return false;
    }
    const actorRole = this.getOrgRole(org);
    if (actorRole === 'OWNER') {
      return true;
    }
    const actorPower = this.getOrgPower(org);
    const newRolePower = this.parseOrgRolePower(newRoleName);
    return actorPower > newRolePower;
  }

  // =========================================================================
  // COMMON ACTION SHORTCUTS
  // =========================================================================

  public canCreateEvent(): boolean {
    if (this.hasSystemPower(SystemPower.MODERATOR)) {
      return true;
    }
    return this.organizationsWithCapability(OrgCapability.ORG_EVENT_CREATE).length > 0;
  }

  public canCreateTable(): boolean {
    if (this.hasSystemPower(SystemPower.MODERATOR)) {
      return true;
    }
    return this.organizationsWithCapability(OrgCapability.ORG_TABLE_CREATE).length > 0;
  }

  public organizationsWithCapability(capability: OrgCapability): Organization[] {
    const organizations = this.getCurrentUser()?.organizations ?? [];
    return organizations.filter(
      (org) => Boolean(org.organizationActive) && this.hasOrgCapability(org, capability),
    );
  }

  public canCreateOrganization(): boolean {
    return this.hasSystemCapability(SystemCapability.CREATE_ORGANIZATION);
  }

  public canEditOrg(org: Organization | undefined | null): boolean {
    return this.hasOrgCapability(org, OrgCapability.ORG_EDIT);
  }

  public canDeleteOrg(org: Organization | undefined | null): boolean {
    return this.hasOrgCapability(org, OrgCapability.ORG_DELETE);
  }

  public canViewOrgMembers(org: Organization | undefined | null): boolean {
    return this.hasOrgCapability(org, OrgCapability.ORG_MEMBER_VIEW);
  }

  public canManageOrgMembers(org: Organization | undefined | null): boolean {
    return (
      this.hasOrgCapability(org, OrgCapability.ORG_MEMBER_INVITE) ||
      this.hasOrgCapability(org, OrgCapability.ORG_MEMBER_MANAGE)
    );
  }

  public canManageOrgContent(
    org: Organization | OrganizationSummary | { id?: number } | undefined | null,
  ): boolean {
    if (this.hasSystemPower(SystemPower.MODERATOR)) {
      return true;
    }
    if (!org?.id) {
      return false;
    }
    const userOrg = this.getCurrentUser()?.organizations?.find((o) => o.id === org.id);
    if (!userOrg || userOrg.organizationActive === false) {
      return false;
    }
    return (
      this.hasOrgCapability(userOrg, OrgCapability.ORG_EVENT_MANAGE) ||
      this.hasOrgPower(userOrg, OrgRolePower.MODERATOR)
    );
  }

  public canViewDisabledEvent(event: Event | undefined | null): boolean {
    if (!event) {
      return false;
    }
    if (event.active !== false) {
      return true;
    }
    if (this.hasSystemPower(SystemPower.MODERATOR)) {
      return true;
    }
    if (event.isCreator) {
      return true;
    }
    return this.canManageOrgContent(event.organization);
  }

  public canManageTable(table: GameTable | undefined | null): boolean {
    if (!table) {
      return false;
    }
    if (this.hasSystemPower(SystemPower.MODERATOR)) {
      return true;
    }
    const currentUserId = this.getCurrentUser()?.id;
    const isCreator = Boolean(
      table.isCreator ||
        (currentUserId != null && table.creator?.userId === currentUserId),
    );
    if (isCreator) {
      return true;
    }
    const org = table.organization ?? table.event?.organization;
    return this.canManageOrgContent(org);
  }
}
